// src/components/homepage_content/LiveMatches.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetSlip } from '../../context/BetSlipContext';
import { FootballAPI } from '../../services/footballApi';
import MatchCard from '../sports/MatchCard';
import { FiRefreshCw, FiChevronRight, FiActivity } from 'react-icons/fi';

const LiveMatches = () => {
  const [matches, setMatches] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [viewMode, setViewMode] = useState('grid');
  
  const navigate = useNavigate();
  const { betslipCount, bets } = useBetSlip();

  // Fetch live matches from API
  const fetchLiveMatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await FootballAPI.getLiveFixtures();
      
      if (response.success && response.data) {
        const liveData = response.data;
        
        if (liveData.length > 0) {
          // Group by league
          const grouped = {};
          liveData.forEach(fixture => {
            const leagueId = fixture.league_id;
            if (!grouped[leagueId]) {
              grouped[leagueId] = {
                league_id: leagueId,
                league_name: fixture.league_name,
                league_logo: fixture.league_logo,
                country: fixture.country,
                fixtures: []
              };
            }
            
            // Format the fixture for MatchCard
            const formattedFixture = {
              ...fixture,
              odds: {
                home: fixture.odds?.home || null,
                draw: fixture.odds?.draw || null,
                away: fixture.odds?.away || null
              },
              isLive: ['1H', 'HT', '2H', 'ET', 'P'].includes(fixture.status),
              status: fixture.status,
              elapsed: fixture.elapsed || 0,
              home_score: fixture.home_score,
              away_score: fixture.away_score
            };
            
            grouped[leagueId].fixtures.push(formattedFixture);
          });
          
          // Sort leagues by priority
          const sortedGroups = Object.values(grouped).sort((a, b) => {
            const priorityA = getLeaguePriority(a.league_id, a.league_name);
            const priorityB = getLeaguePriority(b.league_id, b.league_name);
            return priorityA - priorityB;
          });
          
          setGroupedMatches(sortedGroups);
          setMatches(liveData);
          setLastUpdate(new Date());
        } else {
          setMatches([]);
          setGroupedMatches([]);
          setError('No live matches at the moment');
        }
      } else {
        setError('Failed to load live matches');
      }
    } catch (err) {
      // console.error('Error fetching live matches:', err);
      setError('Failed to load live matches');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // League priority function
  const getLeaguePriority = (leagueId, leagueName) => {
    const priorityMap = {
      1: 1,   // FIFA World Cup
      2: 2,   // UEFA Champions League
      39: 3,  // Premier League
      140: 4, // La Liga
      135: 5, // Serie A
      78: 6,  // Bundesliga
      61: 7,  // Ligue 1
    };
    
    if (priorityMap[leagueId]) {
      return priorityMap[leagueId];
    }
    
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    
    return 999;
  };

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLiveMatches();
  };

  // Handle match card click
  const handleMatchCardClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  // Initial load
  useEffect(() => {
    fetchLiveMatches();
    
    // Auto-refresh every 15 seconds for live matches
    const interval = setInterval(() => {
      if (!loading && !refreshing) {
        fetchLiveMatches();
      }
    }, 15000);
    
    return () => clearInterval(interval);
  }, [fetchLiveMatches]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-900 rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <h2 className="text-xl font-bold text-white">Live Matches</h2>
          </div>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-800 rounded w-1/4"></div>
          <div className="h-32 bg-gray-800 rounded"></div>
          <div className="h-32 bg-gray-800 rounded"></div>
        </div>
      </div>
    );
  }

  // Flatten matches for stats
  const allMatches = groupedMatches.flatMap(group => group.fixtures);
  const liveMatchCount = allMatches.length;

  return (
    <div className="bg-gray-900 rounded-xl p-4 sm:p-6 border border-gray-800">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <FiActivity className="text-red-500" />
            Live Matches
          </h2>
          {liveMatchCount > 0 && (
            <span className="bg-red-500/20 text-red-400 text-xs px-2 py-1 rounded-full">
              {liveMatchCount} live
            </span>
          )}
          {lastUpdate && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              Updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
          >
            <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          
          <button
            onClick={() => navigate('/live')}
            className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
          >
            <span>View All</span>
            <FiChevronRight className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => navigate('/betslip')}
            className="relative px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Betslip
            {betslipCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {betslipCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-red-300 hover:text-red-200 text-sm underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* View Mode Toggle */}
      {allMatches.length > 0 && (
        <div className="flex items-center justify-end gap-2 mb-4">
          <span className="text-xs text-gray-500">View:</span>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-blue-600' : 'bg-gray-800'}`}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-blue-600' : 'bg-gray-800'}`}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      )}

      {/* Matches Grid */}
      {allMatches.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <div className="text-5xl mb-4">📺</div>
          <h3 className="text-white font-bold text-lg mb-2">No Live Matches</h3>
          <p className="text-gray-400 text-sm">Check back later for live action</p>
          <button
            onClick={() => navigate('/sports')}
            className="mt-4 text-blue-400 hover:text-blue-300 text-sm"
          >
            Browse upcoming matches →
          </button>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {groupedMatches.map((group) => (
            <div key={group.league_id} className="space-y-2 sm:space-y-3">
              {/* League Header */}
              <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-2 sm:p-3">
                {group.league_logo && (
                  <img src={group.league_logo} alt={group.league_name} className="w-4 h-4 sm:w-5 sm:h-5 object-contain" />
                )}
                <h3 className="font-bold text-white text-xs sm:text-sm">{group.league_name}</h3>
                <span className="text-[10px] sm:text-xs text-gray-400 bg-gray-700 px-1.5 sm:px-2 py-0.5 rounded-full">
                  {group.fixtures.length}
                </span>
                <span className="ml-auto flex items-center gap-1 text-xs text-red-400">
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                  LIVE
                </span>
              </div>
              
              {/* Matches */}
              <div className={viewMode === 'grid' 
                ? "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-3" 
                : "space-y-2 sm:space-y-3"
              }>
                {group.fixtures.map((match) => (
                  <div 
                    key={match.id} 
                    onClick={() => handleMatchCardClick(match.id)}
                    className="cursor-pointer hover:scale-[1.02] transition-transform"
                  >
                    <MatchCard
                      match={match}
                      sport="football"
                      oddsFormat={oddsFormat}
                      viewMode={viewMode}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Bet Info */}
      <div className="mt-6 pt-6 border-t border-gray-800">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs sm:text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span>Click odds to add to betslip</span>
          </div>
          <div className="flex items-center space-x-3 sm:space-x-4">
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs">1</span> Home
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs">X</span> Draw
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-gray-800 rounded text-xs">2</span> Away
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-xs">Auto-refresh: 15s</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMatches;